import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../../api/client';
import TeamMemberDrawer from './TeamMemberDrawer';
import {
  certDisplayStatus,
  initialsFromName,
  truncateText,
  type TeamMemberDetail,
  type TeamMemberSummary
} from './teamTypes';

function roleBadgeClass(role: string): string {
  return role === 'admin'
    ? 'team-role-badge team-role-badge--admin'
    : 'team-role-badge team-role-badge--technician';
}

function certBadgeClass(status: ReturnType<typeof certDisplayStatus>): string {
  switch (status) {
    case 'expiring':
      return 'team-cert-chip team-cert-chip--expiring';
    case 'expired':
      return 'team-cert-chip team-cert-chip--expired';
    default:
      return 'team-cert-chip';
  }
}

type TeamDirectorySectionProps = {
  refreshKey?: number;
};

export default function TeamDirectorySection({
  refreshKey = 0
}: TeamDirectorySectionProps): React.JSX.Element {
  const [members, setMembers] = useState<TeamMemberSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<TeamMemberDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const path = showInactive ? '/api/team?status=all' : '/api/team';
      const res = await apiRequest<{ data: TeamMemberSummary[] }>(path);
      setMembers(res.data || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message.replace(/^HTTP \d+[^:]+:\s*/, '') : 'Failed to load team.');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers, refreshKey]);

  const openMember = async (memberId: string) => {
    setSelectedId(memberId);
    setDetailLoading(true);
    setSelectedDetail(null);
    try {
      const res = await apiRequest<{ user: TeamMemberDetail }>(`/api/team/${memberId}`);
      setSelectedDetail(res.user);
    } catch {
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDrawer = () => {
    setSelectedId(null);
    setSelectedDetail(null);
  };

  const handleMemberUpdated = (updated: TeamMemberDetail) => {
    setSelectedDetail(updated);
    setMembers((prev) => {
      const summary: TeamMemberSummary = {
        _id: updated._id,
        name: updated.name,
        email: updated.email,
        role: updated.role,
        jobTitle: updated.jobTitle,
        department: updated.department,
        phone: updated.phone,
        avatarBase64: updated.avatarBase64,
        certifications: updated.certifications,
        status: updated.status,
        hiredAt: updated.hiredAt
      };
      const exists = prev.some((m) => m._id === updated._id);
      if (!exists) return [...prev, summary].sort((a, b) => a.name.localeCompare(b.name));
      return prev
        .map((m) => (m._id === updated._id ? summary : m))
        .sort((a, b) => a.name.localeCompare(b.name));
    });
    if (!showInactive && updated.status === 'inactive') {
      setMembers((prev) => prev.filter((m) => m._id !== updated._id));
    }
  };

  const visibleMembers = useMemo(() => {
    if (showInactive) return members;
    return members.filter((m) => m.status === 'active');
  }, [members, showInactive]);

  const showEmpty = !loading && visibleMembers.length === 0;

  return (
    <>
      <section className="quote-card settings-section-card">
        <div className="team-directory-header">
          <div>
            <div className="quote-section-title">Your Team</div>
            <p className="settings-hint">All active members in your workspace.</p>
          </div>
          <label className="team-inactive-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span>Show inactive members</span>
          </label>
        </div>

        {error ? <p className="settings-field-error">{error}</p> : null}

        {loading ? (
          <div className="settings-invite-skeleton" aria-hidden>
            <div className="settings-invite-skeleton-row" />
            <div className="settings-invite-skeleton-row" />
            <div className="settings-invite-skeleton-row" />
          </div>
        ) : showEmpty ? (
          <div className="team-directory-empty">
            <div className="team-directory-empty-icon" aria-hidden>
              👥
            </div>
            <h3>No team members yet</h3>
            <p>Invite your first team member above to get started.</p>
          </div>
        ) : (
          <div className="team-directory-grid">
            {visibleMembers.map((member) => {
              const certs = member.certifications || [];
              const inactive = member.status === 'inactive';
              return (
                <button
                  key={member._id}
                  type="button"
                  className={`team-member-card${inactive ? ' is-inactive' : ''}`}
                  onClick={() => void openMember(member._id)}
                >
                  {inactive ? <span className="team-inactive-overlay">Inactive</span> : null}
                  <div className="team-member-card-top">
                    {member.avatarBase64 ? (
                      <img src={member.avatarBase64} alt="" className="team-member-avatar" />
                    ) : (
                      <div className="team-member-avatar team-member-avatar--initials">
                        {initialsFromName(member.name)}
                      </div>
                    )}
                    <div className="team-member-card-meta">
                      <div className="team-member-name">{member.name}</div>
                      <div className="team-member-title">{member.jobTitle || 'Team member'}</div>
                      <span className={roleBadgeClass(member.role)}>
                        {member.role === 'admin' ? 'Admin' : 'Technician'}
                      </span>
                    </div>
                  </div>
                  <div className="team-member-card-divider" />
                  <div className="team-member-card-contact">
                    <span>📧 {member.email}</span>
                    <span>📞 {member.phone?.trim() || '—'}</span>
                  </div>
                  <div className="team-member-card-certs">
                    {certs.slice(0, 2).map((cert, index) => {
                      const status = certDisplayStatus(cert.expiresAt);
                      return (
                        <span key={`${cert.name}-${index}`} className={certBadgeClass(status)}>
                          {status === 'expired' ? '⚠ ' : ''}
                          {truncateText(cert.name, 20)}
                        </span>
                      );
                    })}
                    {certs.length > 2 ? (
                      <span className="team-cert-more">+{certs.length - 2} more</span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {selectedId ? (
        <TeamMemberDrawer
          memberId={selectedId}
          member={selectedDetail}
          loading={detailLoading}
          onClose={closeDrawer}
          onUpdated={handleMemberUpdated}
        />
      ) : null}
    </>
  );
}
